import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UploadState } from '@app/core/states/upload.state';
import { TrackMobileLineComponent } from '@app/library/components/content/track-mobile-line/track-mobile-line.component';
import { TrashAllComponent } from '@app/library/components/content/trash-all/trash-all.component';
import { TrashAudioLineComponent } from '@app/library/components/content/trash-audio-line/trash-audio-line.component';
import { TrashAudioTableComponent } from '@app/library/components/content/trash-audio-table/trash-audio-table.component';
import { TrashHeaderComponent } from '@app/library/components/content/trash-header/trash-header.component';
import { MobAddToPlaylistModalComponent } from '@app/library/components/modals/mob-add-to-playlist-modal/mob-add-to-playlist-modal.component';
import { MobMoveTrackModalComponent } from '@app/library/components/modals/mob-move-track-modal/mob-move-track-modal.component';
import { PlaylistContextMenuComponent } from '@app/library/components/modals/playlist-context-menu/playlist-context-menu.component';
import { TrackContextMenuComponent } from '@app/library/components/modals/track-context-menu/track-context-menu.component';
import { LibraryRootTrashComponent } from '@app/library/components/shell/library-root-trash/library-root-trash.component';
import { MobCreatePlaylistComponent } from '@app/library/components/sides/mob-create-playlist/mob-create-playlist.component';
import { LibraryModalService } from '@app/library/services/library-modal.service';
import { ColumnsState } from '@app/library/states/columns.state';
import { EditingTrackState } from '@app/library/states/editing-track.state';
import { FiltersState } from '@app/library/states/filters.state';
import { LibraryState } from '@app/library/states/library.state';
import { PlaylistColumnsState } from '@app/library/states/playlist-columns.state';
import { PlaylistsState } from '@app/library/states/playlists.state';
import { RadioTagsState } from '@app/library/states/radio-tags.state';
import { SelectedTracksState } from '@app/library/states/selected-tracks.state';
import { TrackMixPointsState } from '@app/library/states/track-mix-points.state';
import { TracksState } from '@app/library/states/tracks.state';
import { TrashColumnsState } from '@app/library/states/trash-columns.state';
import { MaterialModule } from '@app/material.module';
import { SharedModule } from '@app/shared';
import { DndModule } from '@app/shared/modules/dnd/dnd.module';
import { TranslateModule } from '@ngx-translate/core';
import { NgxsModule } from '@ngxs/store';
import { LibraryWaveformAngularModule } from '@radioking/library/waveform/angular';
import { OnboardingChecklistModule } from '@radioking/onboarding-checklist';
import { SharedPanelManagerModule } from '@radioking/shared/pannel-manager';
import { UiBoxFilterModule } from '@radioking/ui/box-filter';
import { UiCardModule } from '@radioking/ui/card';
import { UiColorChipModule } from '@radioking/ui/color-chip';
import { UiColoredHintModule } from '@radioking/ui/colored-hint';
import { UiCustomModalWrapperModule } from '@radioking/ui/custom-modal-wrapper';
import { UiDatepickerModule } from '@radioking/ui/datepicker';
import { UiInputModule } from '@radioking/ui/input';
import { UiInputColorPickerModule } from '@radioking/ui/input-color-picker';
import { UiLockedIconModule } from '@radioking/ui/locked-icon';
import { UiMobileFabButtonModule } from '@radioking/ui/mobile-fab-button';
import { UiPageTitleModule } from '@radioking/ui/page-title';
import { UiPlaylistIconModule } from '@radioking/ui/playlist-icon';
import { UiSantaModule } from '@radioking/ui/santa';
import { UiTableProcessingModule } from '@radioking/ui/table-processing';
import { UiTrackInputWithSuggestionsModule } from '@radioking/ui/track-input-with-suggestions';
import { KeyFilterModule } from 'primeng/keyfilter';

import { DirectivesModule } from '../../../../../libs/directives/directives.module';
import { PipesModule } from '../../../../../libs/shared/pipes/pipes.module';
import { UiHotkeysHelpModule } from '../../../../../libs/ui/hotkeys-help/src';
import { UiCodeModule } from '../../../../../libs/ui/mobile-close-button/src/lib/ui-code.module';

import { LibraryAllComponent } from './components/content/library-all/library-all.component';
import { LibraryAudioLineComponent } from './components/content/library-audio-line/library-audio-line.component';
import { LibraryAudioTableComponent } from './components/content/library-audio-table/library-audio-table.component';
import { LibraryFilterButtonComponent } from './components/content/library-filter-button/library-filter-button.component';
import { LibraryHeaderComponent } from './components/content/library-header/library-header.component';
import { LibraryHostComponent } from './components/content/library-host/library-host.component';
import { LibraryMobileComponent } from './components/content/library-mobile/library-mobile.component';
import { LibraryUploadDndComponent } from './components/content/library-upload-dnd/library-upload-dnd.component';
import { LibraryUploadTrackComponent } from './components/content/library-upload-track/library-upload-track.component';
import { PlaylistAllComponent } from './components/content/playlist-all/playlist-all.component';
import { PlaylistAudioLineComponent } from './components/content/playlist-audio-line/playlist-audio-line.component';
import { PlaylistAudioTableComponent } from './components/content/playlist-audio-table/playlist-audio-table.component';
import { PlaylistHeaderComponent } from './components/content/playlist-header/playlist-header.component';
import { PlaylistHostComponent } from './components/content/playlist-host/playlist-host.component';
import { PlaylistMobileComponent } from './components/content/playlist-mobile/playlist-mobile.component';
import { TagTracksAllComponent } from './components/content/tag-tracks-all/tag-tracks-all.component';
import { TagTracksHeaderComponent } from './components/content/tag-tracks-header/tag-tracks-header.component';
import { TagTracksTableComponent } from './components/content/tag-tracks-table/tag-tracks-table.component';
import { AddDuplicateModalComponent } from './components/modals/add-duplicate-modal/add-duplicate-modal.component';
import { AddTagModalComponent } from './components/modals/add-tag-modal/add-tag-modal.component';
import { AddToPlaylistModalComponent } from './components/modals/add-to-playlist-modal/add-to-playlist-modal.component';
import { ArchiveTrackModalComponent } from './components/modals/archive-track-modal/archive-track-modal.component';
import { BacPlaylistModalComponent } from './components/modals/bac-playlist-modal/bac-playlist-modal.component';
import { ClearBacModalComponent } from './components/modals/clear-bac-modal/clear-bac-modal.component';
import { CueInputComponent } from './components/modals/cue-input/cue-input.component';
import { DeleteBacModalComponent } from './components/modals/delete-bac-modal/delete-bac-modal.component';
import { DeletePlaylistModalComponent } from './components/modals/delete-playlist-modal/delete-playlist-modal.component';
import { DeleteTagModalComponent } from './components/modals/delete-tag-modal/delete-tag-modal.component';
import { DeleteTrackModalComponent } from './components/modals/delete-track-modal/delete-track-modal.component';
import { DuplicatePlaylistModalComponent } from './components/modals/duplicate-playlist-modal/duplicate-playlist-modal.component';
import { EditMultipleTrackModalComponent } from './components/modals/edit-multiple-track-modal/edit-multiple-track-modal.component';
import { MixPointsModalComponent } from './components/modals/mix-points-modal/mix-points-modal.component';
import { MoveTrackModalComponent } from './components/modals/move-track-modal/move-track-modal.component';
import { PlaylistUploadBoxModalComponent } from './components/modals/playlist-upload-box-modal/playlist-upload-box-modal.component';
import { RemoveTagsModalComponent } from './components/modals/remove-tags-modal/remove-tags-modal.component';
import { LibraryBacGroupComponent } from './components/shell/library-bac-group/library-bac-group.component';
import { LibraryRootAllBacComponent } from './components/shell/library-root-all-bac/library-root-all-bac.component';
import { LibraryRootBacComponent } from './components/shell/library-root-bac/library-root-bac.component';
import { LibrarySbacGroupComponent } from './components/shell/library-sbac-group/library-sbac-group.component';
import { LibrarySbacItemComponent } from './components/shell/library-sbac-item/library-sbac-item.component';
import { LibrarySbacComponent } from './components/shell/library-sbac/library-sbac.component';
import { LibraryShellComponent } from './components/shell/library-shell/library-shell.component';
import { PlaylistListLineComponent } from './components/shell/playlist-list-line/playlist-list-line.component';
import { PlaylistListComponent } from './components/shell/playlist-list/playlist-list.component';
import { FilterNumericInputComponent } from './components/sides/filter-numeric-input/filter-numeric-input.component';
import { FilterPlaylistInputComponent } from './components/sides/filter-playlist-input/filter-playlist-input.component';
import { FilterTagInputComponent } from './components/sides/filter-tag-input/filter-tag-input.component';
import { LibraryCreatePlaylistComponent } from './components/sides/library-create-playlist/library-create-playlist.component';
import { LibraryEditPlaylistComponent } from './components/sides/library-edit-playlist/library-edit-playlist.component';
import { LibraryFilterPanelComponent } from './components/sides/library-filter-panel/library-filter-panel.component';
import { LibraryTrackEditComponent } from './components/sides/library-track-edit/library-track-edit.component';
import { PlaylistAddTrackLineComponent } from './components/sides/playlist-add-track-line/playlist-add-track-line.component';
import { PlaylistAddTrackComponent } from './components/sides/playlist-add-track/playlist-add-track.component';
import { PlaylistCriteriaBoxComponent } from './components/sides/playlist-criteria-box/playlist-criteria-box.component';
import { PlaylistCriteriaDateComponent } from './components/sides/playlist-criteria-date/playlist-criteria-date.component';
import { PlaylistCriteriaNumberComponent } from './components/sides/playlist-criteria-number/playlist-criteria-number.component';
import { PlaylistCriteriaTagComponent } from './components/sides/playlist-criteria-tag/playlist-criteria-tag.component';
import { PlaylistCriteriaTextComponent } from './components/sides/playlist-criteria-text/playlist-criteria-text.component';
import { TagEditComponent } from './components/sides/tag-edit/tag-edit.component';
import { TrackDetailsComponent } from './components/sides/track-details/track-details.component';
import { TrackImageUploadComponent } from './components/sides/track-image-upload/track-image-upload.component';
import { LibraryHostDirective } from './directives/library-host.directive';
import { LibraryRoutingModule } from './library-routing.module';
import { PlaylistTracksState } from './states/playlist-tracks.state';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    SharedModule,
    MaterialModule,
    ScrollingModule,
    KeyFilterModule,
    LibraryRoutingModule,
    SharedPanelManagerModule,
    DndModule,
    NgxsModule.forFeature([
      ColumnsState,
      TracksState,
      SelectedTracksState,
      UploadState,
      LibraryState,
      PlaylistsState,
      PlaylistTracksState,
      EditingTrackState,
      RadioTagsState,
      FiltersState,
      PlaylistColumnsState,
      TrackMixPointsState,
      TrashColumnsState,
    ]),
    UiDatepickerModule,
    UiCardModule,
    UiTrackInputWithSuggestionsModule,
    UiInputColorPickerModule,
    LibraryWaveformAngularModule,
    UiColoredHintModule,
    UiColorChipModule,
    UiTableProcessingModule,
    DirectivesModule,
    PipesModule,
    UiHotkeysHelpModule,
    UiInputModule,
    UiSantaModule,
    UiCustomModalWrapperModule,
    UiPlaylistIconModule,
    OnboardingChecklistModule,
    UiPageTitleModule,
    UiLockedIconModule,
    UiBoxFilterModule,
    UiMobileFabButtonModule,
    UiCodeModule,
  ],
  declarations: [
    LibraryShellComponent,
    LibraryAllComponent,
    LibraryAudioLineComponent,
    LibraryAudioTableComponent,
    LibrarySbacComponent,
    LibrarySbacGroupComponent,
    LibrarySbacItemComponent,
    LibraryRootBacComponent,
    LibraryRootTrashComponent,
    TrashAllComponent,
    TrashHeaderComponent,
    TrashAudioTableComponent,
    TrashAudioLineComponent,
    DeleteBacModalComponent,
    ClearBacModalComponent,
    LibraryRootAllBacComponent,
    LibraryTrackEditComponent,
    LibraryBacGroupComponent,
    LibraryHeaderComponent,
    LibraryUploadTrackComponent,
    ArchiveTrackModalComponent,
    DeleteTrackModalComponent,
    MoveTrackModalComponent,
    LibraryUploadDndComponent,
    PlaylistListLineComponent,
    PlaylistListComponent,
    TrackImageUploadComponent,
    AddTagModalComponent,
    LibraryCreatePlaylistComponent,
    LibraryEditPlaylistComponent,
    PlaylistAllComponent,
    PlaylistHeaderComponent,
    AddDuplicateModalComponent,
    AddToPlaylistModalComponent,
    PlaylistAudioTableComponent,
    PlaylistAudioLineComponent,
    PlaylistAddTrackComponent,
    LibraryFilterPanelComponent,
    LibraryFilterButtonComponent,
    FilterTagInputComponent,
    FilterPlaylistInputComponent,
    FilterNumericInputComponent,
    PlaylistAddTrackLineComponent,
    PlaylistCriteriaTextComponent,
    PlaylistCriteriaNumberComponent,
    PlaylistCriteriaBoxComponent,
    PlaylistCriteriaTagComponent,
    PlaylistCriteriaDateComponent,
    DeletePlaylistModalComponent,
    MixPointsModalComponent,
    CueInputComponent,
    DuplicatePlaylistModalComponent,
    TagTracksAllComponent,
    TagTracksHeaderComponent,
    TagTracksTableComponent,
    TagEditComponent,
    DeleteTagModalComponent,
    RemoveTagsModalComponent,
    PlaylistUploadBoxModalComponent,
    EditMultipleTrackModalComponent,
    LibraryHostComponent,
    LibraryHostDirective,
    LibraryMobileComponent,
    PlaylistHostComponent,
    PlaylistMobileComponent,
    TrackMobileLineComponent,
    BacPlaylistModalComponent,
    TrackDetailsComponent,
    MobCreatePlaylistComponent,
    TrackContextMenuComponent,
    MobAddToPlaylistModalComponent,
    MobMoveTrackModalComponent,
    PlaylistContextMenuComponent,
  ],
  providers: [LibraryModalService],
})
export class LibraryModule {}
